import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  useAiAgentList,
  aiAgentDelete,
  aiAgentListGet,
  voiceProvidersOptionsGet,
  modalProviderOptionsGet,
  sttProviderOptionsGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const AiAgents = () => {
  const dispatch = useDispatch();
  const aiAgentList = useAiAgentList();
  const navigate = useNavigate();

  const userRolePermission = useUserRolePermission();

  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchAiAgentList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter, pagesize: filter?.limit, pageno: filter?.page };
      delete payload?.limit;
      delete payload?.page;
      await dispatch(aiAgentListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteAiAgent = async () => {
    try {
      const response = await dispatch(aiAgentDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (aiAgentList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchAiAgentList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchVoiceProvidersOptions = async () => {
    try {
      await dispatch(voiceProvidersOptionsGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchModalProviderOptions = async () => {
    try {
      await dispatch(modalProviderOptionsGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchSttProviderOptions = async () => {
    try {
      await dispatch(sttProviderOptionsGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchVoiceProvidersOptions();
    fetchModalProviderOptions();
    fetchSttProviderOptions();
  }, []);

  useEffect(() => {
    fetchAiAgentList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="AI Agents"
        isExpandSearch
        isAdd
        onAddClick={() => navigate('/ai-callex/ai/ai-agents/new')}
        isAddDisabled={!checkModulePermission('is_add', 'AI Agents', userRolePermission)}
        addBtnName="Add AI Agents"
        searchPlaceholder="Search AI Agents"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : aiAgentList?.data?.length > 0 &&
        checkModulePermission('is_view', 'AI Agents', userRolePermission) ? (
        <div className="p-0 position-relative m-0">
          <div
            style={{
              height: 'calc(100vh - 210px)',
              overflowY: 'auto'
            }}>
            <div
              className="d-grid gap-3 py-2"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '20px'
              }}>
              {aiAgentList?.data?.map((agent) => (
                <Card key={agent.id} className="ai-agent-card">
                  {/* Header */}
                  <div className="ai-agent-card__header">
                    <h5 className="ai-agent-card__title">{agent.name}</h5>

                    <div className="ai-agent-card__actions">
                      <Icon.Edit
                        size={16}
                        className={`cursor-pointer text-info ${
                          !checkModulePermission('is_view', 'AI Agents', userRolePermission) &&
                          'disabled'
                        }`}
                        onClick={() => navigate(`/ai-callex/ai/ai-agents/${agent.id}/edit`)}
                      />
                      <Icon.Trash
                        size={16}
                        className={`cursor-pointer text-danger ${
                          !checkModulePermission('is_delete', 'AI Agents', userRolePermission) &&
                          'disabled'
                        }`}
                        onClick={() => setIsDeleteId(agent.id)}
                      />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="ai-agent-card__body">
                    <div className="agent-row">
                      <span>LLM:</span>
                      <strong>{agent.llm_service}</strong>
                    </div>

                    <div className="agent-row">
                      <span>Voice:</span>
                      <strong>{agent.voice_provider}</strong>
                    </div>

                    <div className="agent-row">
                      <span>Knowledge Base Files:</span>
                      <strong>{agent.attach_file_ids?.length || 0}</strong>
                    </div>

                    <div className="agent-row">
                      <span>Web Search:</span>
                      <strong>{agent.enable_web_search ? 'Enabled' : 'Disabled'}</strong>
                    </div>

                    <div className="agent-row">
                      <span>Post-call ({agent.post_call_config_ids?.length || 0}):</span>
                      <strong>
                        {agent.post_call_config_ids?.length > 0
                          ? agent.post_call_config_ids
                              .map((p) =>
                                p.webhook_url ? 'Webhook' : p.destination ? 'Email' : 'Other'
                              )
                              .join(', ')
                          : 'None'}
                      </strong>
                    </div>

                    <div className="agent-row">
                      <span>Integrations ({agent.integrations?.length || 0}):</span>
                      <strong>
                        {agent.integrations?.length > 0
                          ? agent.integrations.map((i) => i.name).join(', ')
                          : 'None'}
                      </strong>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="ai-agent-card__footer">
                    <div className="ai-agent-card__tags">
                      {agent.bot_type && (
                        <span className="agent-badge">
                          {agent.bot_type === 'prompt' ? 'Text Based' : agent.bot_type}
                        </span>
                      )}

                      {agent.bot_call_type && (
                        <span className="agent-badge">{agent.bot_call_type}</span>
                      )}
                    </div>

                    <span className="agent-id">ID: #{agent.id}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={aiAgentList?.total}
              limit={aiAgentList?.limit}
              page={aiAgentList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchAiAgentList()}
            />
          </div>
        </div>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteAiAgent()}
      />
    </div>
  );
};

export default AiAgents;
